import { apiFetch, query } from "@/lib/api";
import { personalMoney, type PersonalAccount, type PersonalFilter, type PersonalOperation } from "@/lib/personal";

export type PersonalFlow = "spending" | "transfer" | "income" | "unclassified";
export type ExpenseFilter = PersonalFilter & { currency?: string; category_name?: string; uncategorized?: "true" | "false"; flow?: PersonalFlow };
export type FilterPatch = { [K in keyof ExpenseFilter]?: ExpenseFilter[K] | undefined };
export type ExpenseOperation = PersonalOperation & { flow: PersonalFlow };
type MoneyTotals = { spend: string; refund: string; net: string; count: number };
export type ExpenseOverview = {
  totals: (MoneyTotals & { currency: string; review_count: number; uncategorized_count: number })[];
  categories: (MoneyTotals & { name: string | null; currency: string })[];
  accounts: (MoneyTotals & { account_id: number; currency: string })[];
  flows: { flow: PersonalFlow; currency: string; outflow: string; inflow: string; net: string; count: number }[];
  period: { first_date: string | null; last_date: string | null };
  operation_count: number; spending_count: number; review_count: number; uncategorized_count: number; unclassified_count: number;
};
export const FLOW_NAMES: Record<PersonalFlow, string> = {
  spending: "Расходы и возвраты", transfer: "Между своими счетами", income: "Поступления", unclassified: "Вид не определён",
};
export const PERSONAL_QUERY = ["personal"] as const;
export function loadExpenseOverview(filter: ExpenseFilter, signal?: AbortSignal) {
  const { account_id, since, until, currency } = filter;
  return apiFetch<ExpenseOverview>(`/api/personal/overview${query({ account_id, since, until, currency })}`, signal ? { signal } : undefined);
}
export function setPersonalFlow(id: number, flow: PersonalFlow) {
  return apiFetch<ExpenseOperation>(`/api/personal/operations/${id}/flow`, { method: "PATCH", body: JSON.stringify({ flow }) });
}
export function moneyText(value: string) { return personalMoney(value).replace(/^\+/, ""); }
export function day(iso: string) { return iso.split("-").reverse().join("."); }
export function providerName(provider: string) {
  return ({ okx: "OKX Card", bybit: "Bybit Card", bangkok: "Bangkok Bank", kasikorn: "Kasikornbank" } as Record<string, string>)[provider] ?? provider;
}
export function accountName(account: PersonalAccount) {
  const provider = providerName(account.provider);
  return account.name && account.name !== account.account_key ? account.name : provider;
}
/** Ratios use bounded integers only; financial values never pass through Number. */
export function decimalUnits(value: string): bigint {
  const negative = value.startsWith("-");
  const [whole = "0", fraction = ""] = value.replace(/^[+-]/, "").split(".");
  const units = BigInt(whole) * 100_000_000n + BigInt(fraction.padEnd(8, "0").slice(0, 8));
  return negative ? -units : units;
}
export function percentage(amount: string, total: string) {
  const denominator = decimalUnits(total);
  if (denominator <= 0n) return 0;
  return Number(decimalUnits(amount) * 1000n / denominator) / 10;
}
export function matchesOperation(op: ExpenseOperation, filter: ExpenseFilter) {
  return (!filter.review_required || String(op.review_required) === filter.review_required)
    && (!filter.flow || op.flow === filter.flow)
    && (!filter.category_name || op.category_name === filter.category_name)
    && (!filter.uncategorized || (op.category_id === null) === (filter.uncategorized === "true"));
}

export function operationCount(count: number) {
  const last = count % 10, hundred = count % 100;
  const word = last === 1 && hundred !== 11 ? "операция" : last >= 2 && last <= 4 && (hundred < 12 || hundred > 14) ? "операции" : "операций";
  return `${count} ${word}`;
}
